// src/pages/builder/StateAvailabilityPanel.js
//
// Which states and subjects the portal offers, as a switch instead of a deploy.
//
// WHY THIS EXISTS
// ---------------
// Availability used to be build-time constants in src/config/geography.js:
// AVAILABLE_STATES, SCIENCE_READY_STATES, MATH_READY_STATES. Turning West
// Virginia's science on after seeding 130 topics meant editing a constant,
// rebuilding and redeploying — so finished content sat invisible in the gap.
// That came up three separate times in one week of standards work.
//
// Reads and writes `stateSettings` ONLY. It never touches portalLayouts,
// topicLibrary, users, or subscriptions, so nothing here can change a saved
// page or a person's account.
//
// WHAT SWITCHING SOMETHING OFF ACTUALLY DOES
// ------------------------------------------
// It closes that state or subject to NEW signups. It does not disable anyone
// already using it. Someone who paid should not lose access because of an
// internal content decision, and a person locked out of an account they are
// paying for cannot fix it themselves — so that stays a separate, deliberate
// admin action against the account, never a side effect of a content flag.
//
// The banner at the top of the panel says this, because the button is one
// click and the consequence is not obvious from the label.
//
// THREE STATUSES, NOT A CHECKBOX
// ------------------------------
//   ready         seeded and live
//   coming-soon   in the picker, greyed, honest about not being built
//   hidden        not offered
//
// "coming-soon" is the reason this is a dropdown rather than a toggle. A state
// with no content should not silently vanish: a Georgia teacher who looks and
// finds nothing assumes the site is broken, where one who sees "coming soon"
// learns something true and may come back.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  loadStateSettings,
  allStateSettings,
  saveStateAvailable,
  saveSubjectStatus,
} from "../../services/stateSettingsService";
import "./layoutBuilderLibrary.css";

const SUBJECTS = [
  { key: "science", label: "Science" },
  { key: "math", label: "Math" },
  { key: "socialstudies", label: "Social Studies" },
];

const STATUSES = [
  { value: "ready", label: "Ready" },
  { value: "coming-soon", label: "Coming soon" },
  { value: "hidden", label: "Hidden" },
];

// Reuses the availability pills already defined in layoutBuilderLibrary.css
// rather than introducing a second colour vocabulary for the same idea.
const pillClass = (status) =>
  status === "ready"
    ? "lb-avail lb-avail-live"
    : status === "coming-soon"
    ? "lb-avail lb-avail-part"
    : "lb-avail lb-avail-soon";

export default function StateAvailabilityPanel() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("offered");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      await loadStateSettings();
      const all = allStateSettings();
      // Null means the collection has not been seeded. Say so plainly rather
      // than rendering an empty table that looks like every state is off.
      setRows(all);
    } catch (err) {
      setError(err?.message || "Could not load state settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && rows === null && !loading) refresh();
  }, [open, rows, loading, refresh]);

  const visible = useMemo(() => {
    if (!rows) return [];
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === "offered" && !r.available) return false;
      if (filter === "ready" && !SUBJECTS.some((s) => r.subjects?.[s.key]?.status === "ready"))
        return false;
      if (!q) return true;
      return (
        String(r.stateName).toLowerCase().includes(q) ||
        String(r.state).toLowerCase().includes(q)
      );
    });
  }, [rows, filter, query]);

  const onAvailable = async (row, next) => {
    setBusy(`${row.state}:available`);
    setError("");
    try {
      await saveStateAvailable(row.state, next);
      setRows(allStateSettings()?.slice() || null);
    } catch (err) {
      setError(`${row.stateName}: ${err?.message || "save failed"}`);
    } finally {
      setBusy("");
    }
  };

  const onStatus = async (row, subject, status) => {
    setBusy(`${row.state}:${subject}`);
    setError("");
    try {
      await saveSubjectStatus(row.state, subject, status);
      setRows(allStateSettings()?.slice() || null);
    } catch (err) {
      setError(`${row.stateName} ${subject}: ${err?.message || "save failed"}`);
    } finally {
      setBusy("");
    }
  };

  const counts = useMemo(() => {
    if (!rows) return null;
    return {
      offered: rows.filter((r) => r.available).length,
      science: rows.filter((r) => r.subjects?.science?.status === "ready").length,
      math: rows.filter((r) => r.subjects?.math?.status === "ready").length,
      social: rows.filter((r) => r.subjects?.socialstudies?.status === "ready").length,
    };
  }, [rows]);

  return (
    <section className="lb-card lb-library">
      <div className="lb-card-head">
        <span className="lb-step lb-step-alt">◉</span>
        <div>
          <h2>State availability</h2>
          <p className="lb-hint">
            Which states and subjects appear at signup. Changes take effect
            immediately, with no deploy.
          </p>
        </div>
        <button
          type="button"
          className="lb-btn"
          onClick={() => setOpen((v) => !v)}
          style={{ marginLeft: "auto" }}
        >
          {open ? "Hide" : "Open"}
        </button>
      </div>

      {open && (
        <div className="lb-avail-panel">
          {/* The consequence of the switch is not obvious from the label, so
              it is stated rather than left to be discovered. */}
          <p className="lb-hint lb-avail-warning">
            Switching something off closes it to <strong>new signups only</strong>.
            Everyone already using it keeps working exactly as before — no
            existing account is ever disabled from this panel.
          </p>

          {error && (
            <p className="lb-hint" style={{ color: "var(--lb-red)" }}>
              {error}
            </p>
          )}

          {loading && <p className="lb-hint">Loading…</p>}

          {!loading && rows === null && (
            <p className="lb-hint">
              No <code>stateSettings</code> collection yet. Seed it first:
              <br />
              <code>node src/scripts/state-status.mjs --init --write</code>
              <br />
              Until then the portal uses the constants in{" "}
              <code>config/geography.js</code>, which is current behaviour.
            </p>
          )}

          {!loading && rows && (
            <>
              {counts && (
                <p className="lb-hint">
                  {counts.offered} state{counts.offered === 1 ? "" : "s"} offered ·
                  science ready in {counts.science} · math in {counts.math} ·
                  social studies in {counts.social}
                </p>
              )}

              <div className="lb-avail-controls">
                <div className="lb-seg" role="group" aria-label="Filter states">
                  {[
                    { k: "offered", l: "Offered" },
                    { k: "ready", l: "Has content" },
                    { k: "all", l: "All 51" },
                  ].map((f) => (
                    <button
                      key={f.k}
                      type="button"
                      className={`lb-btn ${filter === f.k ? "lb-btn-on" : ""}`}
                      onClick={() => setFilter(f.k)}
                    >
                      {f.l}
                    </button>
                  ))}
                </div>
                <input
                  className="lb-input"
                  placeholder="Find a state…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  aria-label="Find a state"
                />
                <button type="button" className="lb-btn" onClick={refresh}>
                  Refresh
                </button>
              </div>

              <table className="lb-avail-table">
                <thead>
                  <tr>
                    <th>State</th>
                    <th>Offered</th>
                    {SUBJECTS.map((s) => (
                      <th key={s.key}>{s.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visible.map((row) => (
                    <tr key={row.state} className={row.available ? "" : "lb-avail-rowoff"}>
                      <td>
                        <strong>{row.stateName}</strong>{" "}
                        <span className="lb-code">{String(row.state).toUpperCase()}</span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className={pillClass(row.available ? "ready" : "hidden")}
                          disabled={busy === `${row.state}:available`}
                          onClick={() => onAvailable(row, !row.available)}
                          title={
                            row.available
                              ? "Offered at signup. Click to close to new signups."
                              : "Not offered. Click to open to new signups."
                          }
                        >
                          {busy === `${row.state}:available`
                            ? "…"
                            : row.available
                            ? "Offered"
                            : "Closed"}
                        </button>
                      </td>
                      {SUBJECTS.map((s) => {
                        const status = row.subjects?.[s.key]?.status || "hidden";
                        return (
                          <td key={s.key}>
                            <select
                              className={`lb-select ${pillClass(status)}`}
                              value={status}
                              disabled={busy === `${row.state}:${s.key}`}
                              onChange={(e) => onStatus(row, s.key, e.target.value)}
                              aria-label={`${row.stateName} ${s.label} status`}
                            >
                              {STATUSES.map((st) => (
                                <option key={st.value} value={st.value}>
                                  {st.label}
                                </option>
                              ))}
                            </select>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {!visible.length && (
                    <tr>
                      <td colSpan={2 + SUBJECTS.length} className="lb-hint">
                        No states match that filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </section>
  );
}
